import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useLogout } from '@/features/auth/use-auth';
import { ApiError } from '@/lib/api';
import { setLaunchFailure } from '@/lib/launch-failure';
import { safePortalReturn } from '@/lib/navigation';

/**
 * Reasons a platform may pass when it signs the employee out because it
 * refused the launch (unified-access.md section 3.2). Anything else is
 * ignored, so the URL can never put arbitrary text on the login form.
 */
const LOGOUT_REASONS = ['NO_PLATFORM_ACCESS', 'NO_BRANCH_ACCESS', 'LAUNCH_FAILED'] as const;
type LogoutReason = (typeof LOGOUT_REASONS)[number];

function isLogoutReason(value: unknown): value is LogoutReason {
  return LOGOUT_REASONS.includes(value as LogoutReason);
}

/**
 * unified-access.md §3 step 6: a platform, after clearing its own session,
 * sends the browser to `http://localhost:5200/logout` so signing out of one
 * platform signs the employee out of Unified Access too. This route is
 * navigable directly (no confirmation, no auth guard — logging out an
 * already-logged-out session is a no-op), calls POST /auth/logout, then
 * always lands on /login.
 *
 * A `returnTo` (and `lock=1`, captured by the root route, section 3.2) is
 * carried to /login, so the next sign-in goes straight back to the platform
 * the employee signed out of. A known `reason` is shown on the login form.
 */
function LogoutPage() {
  const navigate = useNavigate();
  const { returnTo, reason } = Route.useSearch();
  const logout = useLogout();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    logout.mutate(undefined, {
      onSettled: () => {
        if (isLogoutReason(reason)) setLaunchFailure(new ApiError(403, reason, reason));
        void navigate({ to: '/login', search: returnTo ? { returnTo } : {} });
      },
    });
    // Runs once on mount; useLogout()/navigate identities are stable enough
    // for this route's lifetime and re-running would fire a second logout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

export const Route = createFileRoute('/logout')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { returnTo?: string; reason?: LogoutReason } => {
    const returnTo = safePortalReturn(search['returnTo']);
    const reason = search['reason'];
    // Explicit keys: the router merges the raw query under this result, so
    // an omitted key would let the unvalidated value through.
    return {
      returnTo: returnTo === '/' ? undefined : returnTo,
      reason: isLogoutReason(reason) ? reason : undefined,
    };
  },
  component: LogoutPage,
});
